import { useState, useEffect, useCallback, useRef } from 'react';
import { measureLatency, getStabilityScore } from '../utils/networkUtils';

const ENDPOINTS = {
  CLOUDFLARE: 'https://1.1.1.1',
  GOOGLE: 'https://dns.google',
  QUAD9: 'https://dns.quad9.net',
};

const STORAGE_KEY = 'nettune_daily_averages_v2';

export const useNetworkStats = (isActive = false) => {
  const [stats, setStats] = useState({
    ping: 0,
    jitter: 0,
    packetLoss: 0,
    stability: 100,
    history: [],
    dnsLatencies: { '1.1.1.1': null, '8.8.8.8': null, '9.9.9.9': null },
    dailyAverages: {
      '1.1.1.1': 0, '8.8.8.8': 0, '9.9.9.9': 0,
      globalLatency: 0,
      globalJitter: 0,
      globalScore: 0
    },
    provider: {
      ip: 'Loading...',
      isp: 'Detecting...',
      asn: '---',
      location: 'Detecting...',
      city: '',
      country: ''
    },
  });

  const historyRef = useRef([]);
  const totalsRef = useRef(JSON.parse(localStorage.getItem(STORAGE_KEY)) || {
    '1.1.1.1': { sum: 0, count: 0 },
    '8.8.8.8': { sum: 0, count: 0 },
    '9.9.9.9': { sum: 0, count: 0 },
    global: { latSum: 0, jitSum: 0, scoreSum: 0, count: 0 },
    date: new Date().toDateString()
  });

  // Fetch Provider Info once on mount
  useEffect(() => {
    const fetchProviderData = async () => {
      try {
        const res = await fetch('https://ipapi.co/json/');
        const data = await res.json();
        setStats(s => ({
          ...s,
          provider: {
            ip: data.ip,
            isp: data.org || data.asn_org || 'Unknown ISP',
            asn: data.asn || '---',
            location: `${data.city}, ${data.region}`,
            city: data.city,
            country: data.country_name
          }
        }));
      } catch (err) {
        setStats(s => ({
          ...s,
          provider: { ...s.provider, isp: 'Privacy Shield Active', ip: 'Hidden' }
        }));
      }
    };
    fetchProviderData();
  }, []);

  useEffect(() => {
    if (totalsRef.current.date !== new Date().toDateString()) {
      totalsRef.current = {
        '1.1.1.1': { sum: 0, count: 0 },
        '8.8.8.8': { sum: 0, count: 0 },
        '9.9.9.9': { sum: 0, count: 0 },
        global: { latSum: 0, jitSum: 0, scoreSum: 0, count: 0 },
        date: new Date().toDateString()
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(totalsRef.current));
    }
  }, []);

  const runTest = useCallback(async () => {
    if (!isActive) return;

    const pingValues = [];
    let failures = 0;
    const burstCount = 2;

    for (let i = 0; i < burstCount; i++) {
      const p = await measureLatency(ENDPOINTS.CLOUDFLARE);
      if (p !== -1) pingValues.push(p);
      else failures = failures + 1;
    }

    const avgPing = pingValues.length > 0 ? Math.round(pingValues.reduce((a, b) => a + b, 0) / pingValues.length) : 0;
    const jitter = pingValues.length > 1 ? Math.round(Math.abs(pingValues[0] - pingValues[1])) : 0;
    const packetLoss = Math.round((failures / burstCount) * 100);
    const score = getStabilityScore(avgPing, jitter, packetLoss);

    const d1 = await measureLatency(ENDPOINTS.CLOUDFLARE);
    const d2 = await measureLatency(ENDPOINTS.GOOGLE);
    const d3 = await measureLatency(ENDPOINTS.QUAD9);
    const dnsRes = { '1.1.1.1': d1, '8.8.8.8': d2, '9.9.9.9': d3 };

    Object.keys(dnsRes).forEach(key => {
      if (dnsRes[key] > 0) {
        totalsRef.current[key].sum += dnsRes[key];
        totalsRef.current[key].count += 1;
      }
    });

    if (avgPing > 0) {
      totalsRef.current.global.latSum += avgPing;
      totalsRef.current.global.jitSum += jitter;
      totalsRef.current.global.scoreSum += score;
      totalsRef.current.global.count += 1;
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(totalsRef.current));

    const gCount = totalsRef.current.global.count || 1;
    const dailyAverages = {
      '1.1.1.1': Math.round(totalsRef.current['1.1.1.1'].sum / (totalsRef.current['1.1.1.1'].count || 1)),
      '8.8.8.8': Math.round(totalsRef.current['8.8.8.8'].sum / (totalsRef.current['8.8.8.8'].count || 1)),
      '9.9.9.9': Math.round(totalsRef.current['9.9.9.9'].sum / (totalsRef.current['9.9.9.9'].count || 1)),
      globalLatency: Math.round(totalsRef.current.global.latSum / gCount),
      globalJitter: Math.round(totalsRef.current.global.jitSum / gCount),
      globalScore: Math.round(totalsRef.current.global.scoreSum / gCount),
    };

    const now = new Date();
    const newPoint = {
      time: now.toLocaleTimeString([], { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }),
      ping: avgPing
    };
    const newHistory = [...historyRef.current, newPoint].slice(-20);
    historyRef.current = newHistory;

    setStats(prev => ({
      ...prev,
      ping: avgPing,
      jitter,
      packetLoss,
      stability: score,
      history: newHistory,
      dnsLatencies: dnsRes,
      dailyAverages
    }));
  }, [isActive]);

  useEffect(() => {
    if (isActive) {
      runTest();
      const interval = setInterval(runTest, 8000);
      return () => clearInterval(interval);
    }
  }, [isActive, runTest]);

  return { stats, refetch: runTest };
};